import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import {
  MapPin, Award, Lock, ChevronDown, ChevronUp, Sparkles, Star,
  Trophy, Medal, TrendingUp
} from 'lucide-react';
import { FramedAvatar, StyledUsername } from '../components/cosmetics';

// ─── Rankings (formerly Leaderboard) ────────────────────────────
const Rankings = ({ user }) => {
  const [pointsLeaderboard, setPointsLeaderboard] = useState([]);
  const [badgeLeaderboard, setBadgeLeaderboard] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('points');
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      try {
        const [pointsRes, badgesRes] = await Promise.all([
          api.get('/leaderboard?limit=50'),
          api.get('/badges/leaderboard?limit=50'),
        ]);
        const pl = pointsRes.data.leaderboard || [];
        setPointsLeaderboard(pl);
        setBadgeLeaderboard(badgesRes.data?.leaderboard || badgesRes.data || []);
        if (user) {
          const idx = pl.findIndex(e => e.user_id === user.id);
          if (idx !== -1) setUserRank(idx + 1);
        }
      } catch {
        // silently fail — leaderboard is non-critical
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboards();
  }, [user]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-gray-600 font-mono text-sm w-5 text-center">{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-900/30 to-transparent border-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-gray-700/30 to-transparent border-gray-500/30';
    if (rank === 3) return 'bg-gradient-to-r from-amber-900/30 to-transparent border-amber-600/30';
    return 'bg-[#0a0a0a] border-[#1a1a1a]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C5A52A]" />
      </div>
    );
  }

  return (
    <div>
      {/* User rank banner */}
      {user && userRank && (
        <div className="bg-green-900/20 border border-green-800/50 rounded-sm p-4 mb-6 flex items-center gap-3">
          <Award className="w-5 h-5 text-green-400 flex-shrink-0" />
          <span className="text-green-400 font-mono text-sm">
            Your rank: <strong>#{userRank}</strong> out of {pointsLeaderboard.length} civic champions
          </span>
        </div>
      )}

      {/* Sub-tabs: Points | Badges */}
      <div className="flex gap-1 mb-6 border-b border-[#1a1a1a]">
        {[['points', 'Points Leaders'], ['badges', 'Badge Collectors']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveSubTab(key)}
            className={`px-5 py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
              activeSubTab === key
                ? 'text-[#C5A52A] border-b-2 border-[#C5A52A]'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Points leaderboard */}
      {activeSubTab === 'points' && (
        <div className="space-y-2">
          {pointsLeaderboard.length === 0 ? (
            <div className="text-center py-16 bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm">
              <Trophy className="w-12 h-12 text-gray-800 mx-auto mb-3" />
              <p className="text-gray-500 font-mono text-sm">No one on the leaderboard yet — be first!</p>
            </div>
          ) : (
            pointsLeaderboard.map((entry, index) => {
              const rank = index + 1;
              const isMe = user && entry.user_id === user.id;
              return (
                <div
                  key={entry.user_id}
                  className={`border rounded-sm p-4 flex items-center gap-4 transition-all ${getRankStyle(rank)} ${
                    isMe ? 'ring-1 ring-green-500/50' : ''
                  }`}
                >
                  <div className="w-8 flex justify-center flex-shrink-0">{getRankIcon(rank)}</div>
                  <FramedAvatar
                    user={{ profile_image_url: entry.profile_image_url, name: entry.name, profile_frame: entry.profile_frame }}
                    size={10}
                  />
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${entry.user_id}`} className="hover:text-[#C5A52A] transition-colors block font-bold truncate">
                      <StyledUsername user={{ accent_color: entry.accent_color, name_style: entry.name_style }}>
                        {entry.name}
                      </StyledUsername>
                      {isMe && <span className="text-green-400 text-xs ml-2">(You)</span>}
                    </Link>
                    {entry.username && (
                      <div className="text-xs text-gray-600 font-mono">@{entry.username}</div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xl font-black text-[#C5A52A]">{rank <= 3 ? ['🥇','🥈','🥉'][rank-1] : `#${rank}`}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Badge leaderboard */}
      {activeSubTab === 'badges' && (
        <div className="space-y-2">
          {badgeLeaderboard.length === 0 ? (
            <div className="text-center py-16 bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm">
              <Award className="w-12 h-12 text-gray-800 mx-auto mb-3" />
              <p className="text-gray-500 font-mono text-sm">No badge collectors yet — start earning!</p>
            </div>
          ) : (
            badgeLeaderboard.map((entry, index) => {
              const rank = index + 1;
              return (
                <div key={entry.user_id} className={`border rounded-sm p-4 flex items-center gap-4 ${getRankStyle(rank)}`}>
                  <div className="w-8 flex justify-center flex-shrink-0">{getRankIcon(rank)}</div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${entry.user_id}`} className="font-bold hover:text-[#C5A52A] transition-colors truncate block">
                      {entry.name}
                    </Link>
                    {entry.username && <div className="text-xs text-gray-600 font-mono">@{entry.username}</div>}
                  </div>
                  {/* Badge stack */}
                  <div className="flex -space-x-2 flex-shrink-0">
                    {(entry.badges || []).slice(0, 5).map((badge, i) => (
                      <div key={`badge-${entry.user_id || entry.name}-${i}`} className="w-7 h-7 rounded-full bg-[#1a1a1a] border-2 border-black overflow-hidden flex items-center justify-center">
                        {badge && badge.startsWith('http') ? (
                          <img src={badge} alt="badge" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[#C5A52A] font-bold text-[9px] uppercase">
                            {(badge || '').replace(/[-_]/g, '').slice(0, 2)}
                          </span>
                        )}
                      </div>
                    ))}
                    {(entry.badges || []).length > 5 && (
                      <div className="w-7 h-7 rounded-full bg-[#1a1a1a] border-2 border-black flex items-center justify-center text-[10px] text-gray-500">
                        +{entry.badges.length - 5}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xl font-black text-purple-400">{entry.badge_count || entry.badges?.length || 0}</div>
                    <div className="text-[10px] text-gray-600 font-mono uppercase">badges</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* CTA */}
      <div className="mt-8 bg-[#0a0a0a] border border-[#1a1a1a] rounded-sm p-6 text-center">
        <p className="text-gray-500 font-mono text-sm mb-4">Report issues and engage with your ward to earn points and climb the ranks.</p>
        <Link to="/submit" className="inline-block bg-[#C5A52A] hover:bg-[#d4b43a] text-black font-bold px-6 py-3 transition-colors font-mono text-sm uppercase tracking-wider">
          Report an Issue
        </Link>
      </div>
    </div>
  );
};

// ─── Main Badges Page ────────────────────────────────────────────
const Badges = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'rankings' ? 'rankings' : 'badges'
  );
  const [categories, setCategories] = useState([]);
  const [badges, setBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchBadges();
  }, [user]);

  // Keep URL in sync with active tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'rankings') {
      setSearchParams({ tab: 'rankings' });
    } else {
      setSearchParams({});
    }
  };

  const fetchBadges = async () => {
    try {
      const [legendRes, myBadgesRes] = await Promise.all([
        api.get('/badges/legend-organized'),
        user ? api.get('/badges/my-badges') : Promise.resolve({ data: { badges: [] } })
      ]);

      const organizedData = legendRes.data.organized || legendRes.data;
      const allBadgesFromCategories = [];

      const categoryList = Object.keys(organizedData).map(categoryKey => {
        const categoryData = organizedData[categoryKey];
        const categoryBadges = categoryData.badges || [];
        allBadgesFromCategories.push(...categoryBadges);
        return {
          id: categoryKey,
          name: categoryData.category?.name || categoryKey,
          badges: categoryBadges,
          count: categoryData.count || categoryBadges.length
        };
      });

      setCategories(categoryList);
      setBadges(allBadgesFromCategories);
      setMyBadges(myBadgesRes.data.badges || []);

      const expanded = {};
      categoryList.forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!user) { toast.info('Please sign up to start earning badges!'); return; }
    if (!navigator.geolocation) { toast.error('Geolocation is not supported by your browser'); return; }
    setCheckingIn(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await api.post('/badges/check-in', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          if (response.data.badge_earned) {
            toast.success(`Badge Earned: ${response.data.badge.name}!`, { autoClose: 5000 });
            // Matomo: track badge earned from check-in
            if (window._paq) {
              window._paq.push(['trackEvent', 'Badge', 'Earned', response.data.badge.name]);
              window._paq.push(['trackEvent', 'Engagement', 'Check-in', user?.ward || 'Unknown Ward']);
            }
            fetchBadges();
          } else {
            // Matomo: track check-in attempt (no badge)
            if (window._paq) window._paq.push(['trackEvent', 'Engagement', 'Check-in', user?.ward || 'Unknown Ward']);
            toast.info(response.data.message || 'No badges earned at this location');
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Check-in failed');
        } finally { setCheckingIn(false); }
      },
      (error) => {
        if (error.code === 1) toast.error('Location access denied.');
        else if (error.code === 2) toast.error('Location unavailable.');
        else if (error.code === 3) toast.error('Location request timed out.');
        else toast.error('Unable to get your location.');
        setCheckingIn(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const hasBadge = (badgeId) => myBadges.some(mb => mb.badge_id === badgeId);

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'official': return 'text-purple-400 border-purple-500/50';
      case 'special':  return 'text-yellow-400 border-yellow-500/50';
      case 'legacy':   return 'text-blue-400 border-blue-500/50';
      default:         return 'text-gray-400 border-gray-700';
    }
  };

  const getRarityLabel = (rarity) => {
    switch (rarity) {
      case 'official': return '\uD83D\uDC51 Official';
      case 'special':  return '\u2B50 Special';
      case 'legacy':   return '\uD83C\uDFDB\uFE0F Legacy';
      default:         return 'Standard';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A52A]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Page header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Sparkles className="w-7 h-7 text-[#C5A52A]" />
              <h1 className="font-headline text-5xl md:text-6xl text-white">THE BADGE</h1>
              <Sparkles className="w-7 h-7 text-[#C5A52A]" />
            </div>
            <p className="font-mono text-sm text-gray-500">
              Earn badges around Regina. Climb the ranks. Make your mark.
            </p>
          </div>

          {/* TOP-LEVEL TABS: BADGES | RANKINGS */}
          <div className="flex border-b border-[#1a1a1a] mb-8">
            {[['badges', '\uD83C\uDFC5 BADGES'], ['rankings', '\uD83C\uDFC6 RANKINGS']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleTabChange(key)}
                className={`px-6 py-4 font-display text-sm tracking-wider transition-colors ${
                  activeTab === key
                    ? 'text-[#C5A52A] border-b-2 border-[#C5A52A]'
                    : 'text-gray-500 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* BADGES TAB */}
          {activeTab === 'badges' && (
            <div>
              {/* Check-In Button */}
              <div className="mb-10">
                <div className="max-w-md mx-auto">
                  <button
                    onClick={handleCheckIn}
                    disabled={checkingIn}
                    className={`w-full py-6 font-headline text-2xl uppercase tracking-wider transition-all transform hover:scale-105 ${
                      checkingIn
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#C5A52A] to-[#D4B93A] text-black hover:shadow-lg hover:shadow-[#C5A52A]/50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <MapPin className="w-8 h-8" />
                      {checkingIn ? 'CHECKING IN...' : 'CHECK IN NOW'}
                    </div>
                  </button>
                  <p className="font-mono text-xs text-gray-600 text-center mt-3">
                    {user
                      ? 'Use your current location to check in at badge locations around Regina'
                      : 'Sign up to start earning badges at locations around Regina'}
                  </p>
                </div>
              </div>

              {/* User stats */}
              {user && (
                <div className="mb-10">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { emoji: '\uD83C\uDFC6', value: myBadges.length, label: 'Earned' },
                      { emoji: '\uD83D\uDD12', value: badges.filter(b => b.is_public).length - myBadges.length, label: 'Locked' },
                      { emoji: '\uD83D\uDCCA', value: `${Math.round((myBadges.length / Math.max(badges.filter(b => b.is_public).length, 1)) * 100)}%`, label: 'Complete' },
                      {
                        emoji: '\u2B50',
                        value: myBadges.filter(mb => {
                          const b = badges.find(b => b.id === mb.badge_id);
                          return b && b.rarity === 'special';
                        }).length,
                        label: 'Special'
                      }
                    ].map((stat, i) => (
                      <div key={stat.label} className="bg-[#0a0a0a] border border-[#1a1a1a] p-4 text-center">
                        <div className="text-2xl mb-2">{stat.emoji}</div>
                        <div className="font-headline text-2xl text-white mb-1">{stat.value}</div>
                        <div className="font-mono text-xs text-gray-600">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {myBadges.length > 0 && (
                    <div className="mt-6 bg-gradient-to-r from-[#C5A52A]/10 to-transparent border border-[#C5A52A]/30 p-6">
                      <h3 className="font-headline text-xl text-white mb-4">Your Badges</h3>
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                        {myBadges.slice(0, 6).map((badge) => {
                          const badgeUrl = badge.badge_icon_url || badge.icon_url;
                          return (
                            <div key={badge.id} className="bg-[#111] border border-[#2a2a2a] p-3 text-center">
                              <div className="flex items-center justify-center mb-2">
                                {badgeUrl && badgeUrl.startsWith('http') ? (
                                  <img src={badgeUrl} alt={badge.badge_name} className="w-12 h-12 object-contain" />
                                ) : (
                                  <div className="text-3xl">{badgeUrl || '\uD83C\uDFC5'}</div>
                                )}
                              </div>
                              <p className="font-mono text-[10px] text-gray-400 line-clamp-2">{badge.badge_name}</p>
                            </div>
                          );
                        })}
                      </div>
                      {myBadges.length > 6 && (
                        <p className="font-mono text-xs text-gray-600 text-center mt-3">+ {myBadges.length - 6} more badges</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Non-logged-in CTA */}
              {!user && (
                <div className="mb-10 bg-gradient-to-r from-[#C5A52A]/10 to-transparent border border-[#C5A52A]/30 p-8 text-center">
                  <h3 className="font-headline text-2xl text-white mb-4">Start Your Collection</h3>
                  <p className="font-mono text-sm text-gray-400 mb-6">Create an account to check in and start earning badges!</p>
                  <div className="flex gap-4 justify-center">
                    <Link to="/register" className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black px-8 py-3 font-mono text-sm uppercase tracking-wider transition-colors">Sign Up</Link>
                    <Link to="/login"  className="border border-[#C5A52A] hover:bg-[#C5A52A]/10 text-[#C5A52A] px-8 py-3 font-mono text-sm uppercase tracking-wider transition-colors">Log In</Link>
                  </div>
                </div>
              )}

              {/* Badge Legend */}
              <div className="space-y-6">
                <h2 className="font-headline text-3xl text-white text-center mb-8">BADGE LEGEND</h2>
                {categories.map((category) => {
                  const categoryBadges = category.badges || [];
                  const earnedCount = categoryBadges.filter(b => hasBadge(b.id)).length;
                  const isExpanded = expandedCategories[category.id];
                  return (
                    <div key={category.id} className="bg-[#0a0a0a] border border-[#1a1a1a] overflow-hidden">
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#111] transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <h3 className="font-headline text-xl text-white">{category.name}</h3>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-500">{earnedCount}/{categoryBadges.length}</span>
                            {user && earnedCount > 0 && (
                              <div className="w-24 h-2 bg-gray-800 overflow-hidden">
                                <div className="h-full bg-[#C5A52A]" style={{ width: `${(earnedCount / categoryBadges.length) * 100}%` }} />
                              </div>
                            )}
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
                      </button>
                      {isExpanded && (
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {categoryBadges.map((badge) => {
                              const earned = hasBadge(badge.id);
                              const isLocked = !earned && user;
                              return (
                                <div
                                  key={badge.id}
                                  className={`bg-[#111] border p-4 transition-all ${
                                    earned ? 'border-green-500/50 bg-green-900/10' : !user ? 'border-gray-800 opacity-60' : 'border-gray-800 hover:border-gray-700'
                                  }`}
                                >
                                  <div className="flex items-start gap-4 mb-3">
                                    <div className="relative flex-shrink-0">
                                      {badge.icon_url && badge.icon_url.startsWith('http') ? (
                                        <img
                                          src={badge.icon_url}
                                          alt={badge.name}
                                          className={`w-16 h-16 object-contain transition-all duration-300 ${isLocked || !user ? 'grayscale opacity-40' : ''}`}
                                        />
                                      ) : (
                                        <div className={`text-5xl transition-all duration-300 ${isLocked || !user ? 'grayscale opacity-40' : ''}`}>
                                          {badge.icon_url || '\uD83C\uDFC5'}
                                        </div>
                                      )}
                                      {(isLocked || !user) && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <Lock className="w-6 h-6 text-gray-400" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2 mb-1">
                                        <h4 className="font-mono text-sm text-white font-bold">{badge.name}</h4>
                                        {earned && <Award className="w-4 h-4 text-green-400 flex-shrink-0" />}
                                      </div>
                                      {badge.rarity !== 'standard' && (
                                        <div className={`inline-block px-2 py-0.5 text-xs font-mono mb-2 border ${getRarityColor(badge.rarity)}`}>
                                          {getRarityLabel(badge.rarity)}
                                        </div>
                                      )}
                                      {badge.reward_points > 0 && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <Star className="w-3 h-3 text-[#C5A52A]" />
                                          <span className="font-mono text-[10px] text-[#C5A52A] font-bold">
                                            {badge.reward_points} pts
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {badge.description && (
                                    <p className="font-mono text-xs text-gray-400 mb-3 line-clamp-2">{badge.description}</p>
                                  )}
                                  <div className="space-y-1">
                                    {badge.badge_type === 'location' && badge.venue_name && badge.venue_name.length > 0 && (
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-gray-600">📍</span>
                                        <span className="font-mono text-gray-500">Check in at {badge.venue_name}</span>
                                      </div>
                                    )}
                                    {badge.badge_type === 'activity' && (
                                      <div className="space-y-1">
                                        {badge.required_posts_count && (
                                          <div className="flex items-center gap-2 text-xs">
                                            <span className="text-gray-600">\uD83D\uDCDD</span>
                                            <span className="font-mono text-gray-500">Create {badge.required_posts_count} posts</span>
                                          </div>
                                        )}
                                        {badge.required_comments_count && (
                                          <div className="flex items-center gap-2 text-xs">
                                            <span className="text-gray-600">\uD83D\uDCAC</span>
                                            <span className="font-mono text-gray-500">Write {badge.required_comments_count} comments</span>
                                          </div>
                                        )}
                                        {badge.required_login_days && (
                                          <div className="flex items-center gap-2 text-xs">
                                            <span className="text-gray-600">\uD83D\uDCC5</span>
                                            <span className="font-mono text-gray-500">Active for {badge.required_login_days} days</span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {badge.badge_type === 'collection' && badge.required_badge_ids?.length > 0 && (
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-gray-600">\uD83C\uDFC6</span>
                                        <span className="font-mono text-gray-500">Collect {badge.required_badge_ids.length} badges</span>
                                      </div>
                                    )}
                                    {badge.max_supply && badge.show_scarcity && (
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-yellow-500">\u26A1</span>
                                        <span className="font-mono text-yellow-400">
                                          {badge.max_supply - (badge.current_supply || 0)}/{badge.max_supply} remaining
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {categories.length === 0 && (
                  <div className="text-center py-12">
                    <Award className="w-16 h-16 text-gray-800 mx-auto mb-4" />
                    <p className="font-mono text-sm text-gray-600">No badges available yet. Check back soon!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* RANKINGS TAB */}
          {activeTab === 'rankings' && <Rankings user={user} />}

        </div>
      </div>
    </Layout>
  );
};

export default Badges;
